import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { WorkflowStatus } from '../../../shared/enums/workflow-status';
import { TRANSLATION_KEYS } from '../../../shared/i18n/translation-keys';
import { WebSocketEventModel } from '../models/workflow-event';
import { WorkflowRunHandle } from '../models/workflow-run';

const COMPLETED_STATUSES: readonly WorkflowStatus[] = [
  WorkflowStatus.ESTRAZIONE_LAVORAZIONI_COMPATIBILI_COMPLETATA,
  WorkflowStatus.ESTRAZIONE_LAVORAZIONI_SUGGERITE_COMPLETATA,
  WorkflowStatus.ELABORAZIONE_NUOVO_PREZZO_COMPLETATA,
];

@Injectable({
  providedIn: 'root',
})
export class WorkflowLoading {
  private readonly _translate = inject(TranslateService);

  /**
   * map all the workflow runs that are currently in progress, each one identified by a unique id.
   */
  private readonly _workflowRuns = signal<readonly WorkflowRunHandle[]>([]);

  readonly runs = this._workflowRuns.asReadonly();

  start(message: string): WorkflowRunHandle {
    const workflowRun = this._createRun(message);

    this._workflowRuns.update((runs) => [...runs, workflowRun]);

    return workflowRun;
  }

  remove(id: string): void {
    this._workflowRuns.update((runs) => runs.filter((workflowRun) => workflowRun.id !== id));
  }

  private _createRun(message: string): WorkflowRunHandle {
    const state = signal<WebSocketEventModel>({
      progress: 0,
      message,
      status: WorkflowStatus.START,
      // non ancora noto: valorizzato da setWebSocketStarted() una volta ottenuto dalla POST
      billOfQuantitiesId: 0,
    });
    const webSocketStarted = signal(false);

    const isCompleted = computed(() => COMPLETED_STATUSES.includes(state().status));
    const isError = computed(() => state().status === WorkflowStatus.ERRORE);

    return {
      // internal id to bookkeeping to register runs (WorkflowNotifications.remove):
      id: crypto.randomUUID(),
      state: state.asReadonly(),
      isCompleted,
      isError,
      hasStarted: webSocketStarted.asReadonly(),
      setWebSocketStarted: (billOfQuantitiesId: number) => {
        webSocketStarted.set(true);
        state.update((current) => ({ ...current, billOfQuantitiesId }));
      },
      update: (event: WebSocketEventModel) => {
        state.set({
          progress: event.progress,
          message: event.message,
          status: event.status as WorkflowStatus,
          billOfQuantitiesId: state().billOfQuantitiesId,
        });
        
        if (COMPLETED_STATUSES.includes(event.status as WorkflowStatus) || event.status === WorkflowStatus.ERRORE ) {
          webSocketStarted.set(false);
        }
      },
      setPostCompleted: (message: string) => {
        state.update((current) => ({
          ...current,
          status: WorkflowStatus.POST_COMPLETED,
          message,
          progress: 10,
        }));
      },
      setError: () => {
        state.update((current) => ({
          ...current,
          status: WorkflowStatus.ERRORE,
          message:
            current.message ||
            this._translate.instant(TRANSLATION_KEYS.GENERAL_ERROR.WORKFLOW_ERROR),
        }));
      },
    };
  }
}
